import { useState, useEffect } from 'react'
import { AssetRow } from '../../types/assetTypes';

interface FailedItem {
    serialNumber: string;
    error: any;
    asset: any;
}

interface ParsedAsset {
    [key: string]: string;
}
import { Typography, ProcessStepper } from '@weirgroup/weir-dls'
import { useTranslation } from 'react-i18next'
import { useDarkMode } from '../../hooks/use-darkmode'
import { useAnalyticsEvent } from '../../hooks/useAnalyticsEvent'
import { TemplateStep, UploadStep, FindingsStep, CreateStep, ConfirmedStep } from './components'
import { downloadBulkAssetTemplate } from '../../api/bulk-assets-template.api'
import './styles.scss'
import assetsApi from '../../api/assets.api';
import { UploadAssetValidationResponse } from '../../types/bucketTypes';
import { useGlobalError } from '../../contexts/global-error.context';

interface StepType {
    title: string
    state: 'active' | 'disabled' | 'success' | 'pending' | 'error'
}

const TEMPLATE_HEADERS = [
    'Asset Type',
    'Tag ID',
    'Serial Number',
    'Tag Type',
    'Equipment Type',
    'Attachment Type',
    'Equipment Model',
    'Make',
    'Manufacturer',
    'Weight',
    'Latitude',
    'Longitude',
];

export const UploadAssets = () => {
    const logAnalyticsEvent = useAnalyticsEvent();
    // ...existing code...
    const [parsedAssets, setParsedAssets] = useState<ParsedAsset[]>([]);
    
    useEffect(() => {
        logAnalyticsEvent('user_viewed_upload_assets_page', { description: 'User viewed upload assets page' });
    }, []);
    const mappedAssets: AssetRow[] = parsedAssets.map((asset) => ({
        assetType: asset['Asset Type'] || '',
        tagId: asset['Tag ID'] || '',
        serialNumber: asset['Serial Number'] || '',
        stockNumber: asset['Stock Number'] || asset['Stock Number (Optional)'] || '',
        machineNumber: asset['Machine Number'] || asset['Machine Number (Optional)'] || '',
        tagType: 'satellite',
        equipmentType: asset['Equipment Type'] || asset['Equipment Type (Optional for ESCO)'] || '',
        attachmentType: asset['Attachment Type'] || asset['Attachment Type (Optional for ESCO)'] || '',
        equipmentModel: asset['Equipment Model'] || asset['Equipment Model (Optional for ESCO)'] || '',
        make: asset['Make (Optional for ESCO)'] || '',
        manufacturer: asset['Manufacturer'] || asset['Manufacturer (Optional for ESCO)'] || '',
        weight: asset['Weight (Optional for ESCO)'] || '',
        latitude: asset['Latitude'] || asset['Latitude (Optional)'] || '',
        longitude: asset['Longitude'] || asset['Longitude (Optional)'] || '',
        lipWidth: asset['Lip Width'] || asset['Lip Width (Optional)'] || asset['lip_width'] || asset['lipWidth'] || '',
        class: asset['Class'] || asset['Class (Optional)'] || asset['class'] || '',
        capacity: asset['Capacity'] || asset['Capacity (Optional)'] || asset['capacity'] || '',
    }));

    const [failedItems, setFailedItems] = useState<FailedItem[]>([]);
    const [successCount, setSuccessCount] = useState(0);
    const [failCount, setFailCount] = useState(0);
    const handleCreateStepNext = (result: { successCount: number; failCount: number; failedItems?: FailedItem[] }) => {
        setSuccessCount(result.successCount);
        setFailCount(result.failCount);
        if (result.failedItems) setFailedItems(result.failedItems);
        setCurrentStep(4);
    };
    const { t } = useTranslation()
    const { showError } = useGlobalError();
    const { darkMode } = useDarkMode()
    const [currentStep, setCurrentStep] = useState(0)
    const [validationErrors, setValidationErrors] = useState<any[]>([])
    const [validationWarnings, setValidationWarnings] = useState<any[]>([])
    const [isAcknowledged, setIsAcknowledged] = useState(false)
    const [selectedFile, setSelectedFile] = useState<File | null>(null)
    const [uploadedFileName, setUploadedFileName] = useState<string>("");
    const [uploadError, setUploadError] = useState<string | null>(null)
    const [isValidating, setIsValidating] = useState<boolean>(false)
    const [validationResponse, setValidationResponse] = useState<UploadAssetValidationResponse | null>(null)
    // useNavigate is not used, removed

    // ...existing code...

    let stepStates = ['active', 'disabled', 'disabled', 'disabled', 'disabled']
    if (currentStep === 4) {
        // If on Confirmed step, lock all previous steps
        stepStates = ['disabled', 'disabled', 'disabled', 'disabled', 'active']
    } else {
        stepStates[currentStep] = 'active'
        // Mark completed steps as success
        for (let i = 0; i < currentStep; i++) {
            stepStates[i] = 'success'
        }
    }

    const steps: StepType[] = [
        { title: t('uploadAssets.steps.template'), state: stepStates[0] as any },
        { title: t('uploadAssets.steps.upload'), state: stepStates[1] as any },
        { title: t('uploadAssets.steps.findings'), state: stepStates[2] as any },
        { title: t('uploadAssets.steps.create'), state: stepStates[3] as any },
        { title: t('uploadAssets.steps.confirmed'), state: stepStates[4] as any },
    ]

    const handleDownloadTemplate = async () => {
        // Download the template using the mock API
        try {
            const blob = await downloadBulkAssetTemplate();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'bulk-asset-template.csv';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            showError(t('uploadAssets.errors.downloadTemplate', 'Failed to download template.'));
            console.error(err)
        }
    }

    const handleNext = () => {
        if (currentStep === 0 && isAcknowledged) {
            setCurrentStep(1)
        } else if (currentStep === 1 && selectedFile) {
            // Send file to API
            handleUploadFile();
        } else if (currentStep === 2) {
            setCurrentStep(3)
        }
    }

    const handlePrev = () => {
        if (currentStep > 0) setCurrentStep(currentStep - 1);
    }

    const handleUploadFile = () => {
        setIsValidating(true);
        assetsApi
            .validateAssetUpload(selectedFile)
            .then((result: any) => {
                const data = result?.data?.data;

                if (data) {
                    setValidationResponse(data);
                    setSelectedFile(null);
                    setCurrentStep(2);
                } else {
                    setValidationResponse(null);
                }
            })
            .catch((err) => {
                showError(t('uploadAssets.errors.validation', 'Validation error.'));
                throw err;
            })
            .finally(() => {
                setIsValidating(false);
            });
    }

    const handleFileSelect = (files: File[] | null) => {
        setUploadError(null);
        setParsedAssets([]);
        setValidationErrors([]);
        setValidationWarnings([]);
        if (files && files?.length > 1) {
            setUploadError('Please select one file only.');
            setSelectedFile(null);
            setUploadedFileName("");
            return;
        }
        const file = files && files.length > 0 ? files[0] : null;
        if (!file) {
            setSelectedFile(null);
            setUploadedFileName("");
            return;
        }
        if (!file.name) {
            setUploadError('No file selected.');
            setSelectedFile(null);
            setUploadedFileName("");
            return;
        }
        // Only allow CSV
        const fileName = file.name.toLowerCase();
        const isCSV = fileName.endsWith('.csv');
        if (!isCSV) {
            setUploadError('Only CSV files are allowed.');
            setSelectedFile(null);
            setUploadedFileName("");
            return;
        }

        // Helper function to parse a row with dynamic delimiter
        const parseRow = (row: string, headers: string[], delimiter: string): { [key: string]: string } => {
            const values = row.split(delimiter);
            const obj: { [key: string]: string } = {};
            headers.forEach((header, i) => {
                obj[header.trim()] = (values[i] || '').trim();
            });
            return obj;
        };

        // Parse and validate headers and rows
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                setSelectedFile(file);
                setUploadedFileName(file.name);
                // Parse CSV/TSV and set parsedAssets
                const text = e.target?.result as string;
                const rows = text.split(/\r?\n/).filter(Boolean);
                if (rows.length > 1) {
                    // Detect delimiter: tab if present, else comma
                    const delimiter = rows[0].includes('\t') ? '\t' : ',';
                    const headers = rows[0].split(delimiter);
                    const data = rows.slice(1).map(row => parseRow(row, headers, delimiter));
                    setParsedAssets(data);
                } else {
                    setParsedAssets([]);
                }
            } catch (err) {
                setUploadError(`Failed to read file. Please check the format. ${err}`);
                setSelectedFile(null);
                setUploadedFileName("");
                setParsedAssets([]);
                throw err;
            }
        };
        reader.readAsText(file);
    }

    const handleCheckboxChange = (checked: boolean) => {
        setIsAcknowledged(checked)
    }

    const handleStepClick = (step: StepType, index: number) => {
        // If on Confirmed step, do not allow any step change
        if (currentStep === 4) return;
        // Only allow going back to completed steps or staying on current step
        if (index <= currentStep) {
            setCurrentStep(index)
        }
    }

    const renderStepContent = () => {
        switch (currentStep) {
            case 0:
                return (
                    <TemplateStep
                        darkMode={darkMode}
                        isAcknowledged={isAcknowledged}
                        onCheckboxChange={handleCheckboxChange}
                        onNext={handleNext}
                        onDownloadTemplate={handleDownloadTemplate}
                    />
                );
            case 1:
                return (
                    <UploadStep
                        darkMode={darkMode}
                        selectedFile={selectedFile}
                        onFileSelect={handleFileSelect}
                        onNext={handleNext}
                        onPrev={handlePrev}
                        errorMessage={uploadError}
                        isValidating={isValidating}
                    />
                );
            case 2:
                return (
                    <FindingsStep
                        darkMode={darkMode}
                        fileName={uploadedFileName || "asset_creation_check_sp.csv"}
                        totalAssets={parsedAssets.length}
                        errorsDetected={validationErrors.length}
                        warningsDetected={validationWarnings.length}
                        businessEntitiesReady={parsedAssets.length - validationErrors.length}
                        businessEntitiesCreated={0}
                        validationErrors={validationErrors}
                        validationWarnings={validationWarnings}
                        onContinue={() => setCurrentStep(3)}
                        onPrev={handlePrev}
                        validationResponse={validationResponse}
                    />
                );
            case 3:
                return (
                    <CreateStep
                        darkMode={darkMode}
                        creationNumber={"M-094f1afb-214c-42a2-82da-dc13ba1149e1"}
                        fileName={uploadedFileName || "asset_creation_mock_up.csv"}
                        businessEntities={`${parsedAssets.length} of ${parsedAssets.length}`}
                        assets={mappedAssets}
                        onNext={handleCreateStepNext}
                        onPrev={handlePrev}
                    />
                );
            case 4:
                return (
                    <ConfirmedStep
                        darkMode={darkMode}
                        successCount={successCount}
                        failCount={failCount}
                        failedItems={failedItems}
                        onFinish={() => { window.location.href = '/trackpro'; }}
                    />
                );
            default:
                return null;
        }
    }

    return (
        <div className={`upload-assets-container ${darkMode ? 'dark-mode' : ''}`}>
            <div className="upload-assets-content">
                <div className="header">
                    <Typography
                        variant="H3"
                        color={darkMode ? "white" : "black"}
                    >
                        {t('uploadAssets.title')}
                    </Typography>
                </div>

                <div className="stepper-section">
                    <ProcessStepper
                        steps={steps}
                        darkMode={darkMode}
                        onStepClick={handleStepClick}
                    />
                </div>

                <div className={`content-section ${darkMode ? 'dark-mode' : ''}`}>
                    <div className="step-content">
                        {renderStepContent()}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default UploadAssets
